/** Panel Select / Autocomplete menüleri — SSOT (filtre, form, tablo sayfalama) */

import { PANEL } from "./panelTokens";
import {
  buildSelectScrollProps,
  preventFocusScroll,
  preventSelectMouseDownScroll,
} from "../../utils/preservePageScroll";

const DEFAULT_MENU_ANCHOR = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

/** MUI Select Menu Paper — panel açık tema */
export const panelSelectMenuPaperSx = (options = {}) => {
  const { minWidth = 200, maxHeight = 360, compact = false } = options;

  return {
    mt: 0.75,
    borderRadius: PANEL.radius.cardMd,
    minWidth,
    maxHeight,
    overflow: "auto",
    bgcolor: PANEL.surface,
    border: `1px solid ${PANEL.border}`,
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
    py: compact ? 0.25 : 0.5,
    "& .MuiMenuItem-root": {
      borderRadius: PANEL.radius.chip,
      mx: 0.5,
      my: 0.25,
      minHeight: compact ? 36 : 40,
      fontSize: compact ? "0.8125rem" : "0.875rem",
      fontWeight: 600,
      color: PANEL.text.body,
      transition: "background-color 0.15s ease, color 0.15s ease",
      "&:hover": { bgcolor: PANEL.fill.subtle },
      "&.Mui-selected": {
        bgcolor: PANEL.accent.soft12,
        color: PANEL.accent.text,
        "&:hover": { bgcolor: PANEL.accent.soft18 },
      },
      "&.Mui-focusVisible": {
        bgcolor: PANEL.fill.subtle,
      },
    },
  };
};

export const panelSelectMenuPaperProps = (options = {}) => ({
  elevation: 0,
  sx: panelSelectMenuPaperSx(options),
});

function buildPanelSelectMenuProps(options = {}) {
  const scrollProps = buildSelectScrollProps();
  const { menuPaperOptions, menuProps: menuOverrides } = options;

  return {
    ...scrollProps.MenuProps,
    ...DEFAULT_MENU_ANCHOR,
    disableAutoFocusItem: true,
    ...menuOverrides,
    PaperProps: {
      elevation: 0,
      ...menuOverrides?.PaperProps,
      sx: {
        ...panelSelectMenuPaperSx(menuPaperOptions),
        ...(menuOverrides?.PaperProps?.sx || {}),
      },
    },
  };
}

/** MUI Select — scroll korumalı + panel menü (doğrudan Select veya SelectProps) */
export function panelSelectScrollProps(options = {}) {
  const scrollProps = buildSelectScrollProps();
  return {
    onOpen: scrollProps.onOpen,
    onFocus: preventFocusScroll,
    onMouseDown: preventSelectMouseDownScroll,
    MenuProps: buildPanelSelectMenuProps(options),
  };
}

/** TextField select — SelectProps ile kullanın */
export function panelSelectFieldProps(options = {}) {
  const scrollProps = buildSelectScrollProps();
  return {
    ...scrollProps,
    onFocus: preventFocusScroll,
    onMouseDown: preventSelectMouseDownScroll,
    MenuProps: buildPanelSelectMenuProps(options),
  };
}

/** TablePagination iç Select — kompakt menü */
export function panelTablePaginationSelectProps(options = {}) {
  return panelSelectScrollProps({
    menuPaperOptions: { minWidth: 72, maxHeight: 280, compact: true },
    ...options,
  });
}

/** Yalnızca MenuProps (Autocomplete dışı kullanımlar) */
export function panelSelectMenuProps(options = {}) {
  return panelSelectScrollProps(options).MenuProps;
}

/** Autocomplete dropdown paper */
export const panelAutocompletePaperSx = {
  mt: 0.75,
  borderRadius: PANEL.radius.cardMd,
  bgcolor: PANEL.surface,
  border: `1px solid ${PANEL.border}`,
  boxShadow:
    "0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
  "& .MuiAutocomplete-listbox": {
    py: 0.5,
    maxHeight: 320,
  },
  "& .MuiAutocomplete-option": {
    minHeight: 40,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: PANEL.text.primary,
    borderRadius: PANEL.radius.chip,
    mx: 0.5,
    my: 0.25,
    "&:hover": { bgcolor: PANEL.fill.subtle },
    '&[aria-selected="true"]': {
      bgcolor: `${PANEL.accent.soft12} !important`,
      color: PANEL.accent.text,
      fontWeight: 600,
    },
    "&.Mui-focused": { bgcolor: PANEL.fill.subtle },
  },
};

/** Autocomplete — slotProps.paper + popper */
export function panelAutocompleteSlotProps(options = {}) {
  return {
    popper: {
      placement: "bottom-start",
      sx: { zIndex: (theme) => theme.zIndex.modal },
      ...options.popper,
    },
    paper: {
      elevation: 0,
      sx: panelAutocompletePaperSx,
      ...options.paper,
    },
  };
}
